import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Target, Gem, Users } from "lucide-react";
import SEO from "../components/SEO";
import { PageHero } from "./Services";
import { brand, values, stats, heroImage, inspection } from "../data/site";

const pillars = [
  { icon: Target, title: "Our Mission", text: "Tell Phoenix homeowners what their roof actually needs. Sometimes that's a replacement. More often, on a tile roof, it's new underlayment and the tile you already own." },
  { icon: Gem, title: "Our Standard", text: "Every product named by manufacturer, line and color on the estimate — never \"architectural shingle\" and a price. New flashing and valley metal, not reused." },
  { icon: Users, title: "Our Promise", text: "Free inspections seven days a week, photos of what we found, and a written scope before anyone gets on the roof." },
];

const About = () => {
  return (
    <div>
      <SEO
        title="About Us | Pepla Roofing | Phoenix, AZ"
        description={`${brand.name} is a Phoenix, AZ roofing contractor — repair, replacement, tile underlayment, foam and flat roofs across Maricopa County.`}
      />
      <PageHero
        eyebrow="About Pepla"
        title="Redefining Excellence"
        subtitle="A Phoenix roofing contractor that tells you what your roof actually needs."
        image={heroImage}
      />

      {/* Story */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="rounded-2xl w-full aspect-[4/3] section-dark shadow-2xl flex items-center justify-center p-10">
              <img loading="lazy" decoding="async" src={brand.logo} alt={brand.name} width="820" height="814" className="w-full max-w-sm h-auto object-contain" />
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
              <span className="inline-flex items-center gap-2 bg-brand text-ink font-semibold uppercase tracking-wider text-xs px-3.5 py-2 rounded-md">
                Free Inspections
              </span>
              <span className="text-muted-foreground">{inspection.hours}</span>
              {brand.yearsExperience && (
                <span className="text-muted-foreground">· {brand.yearsExperience} years of roofing experience</span>
              )}
            </div>
          </div>
          <div>
            <span className="kicker">Our Story</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Craftsmanship You Can Trust</h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed mb-4">
              {brand.name} was built around one idea: most Phoenix homeowners who are told they need a new roof don't. On a Valley tile roof, the tile usually outlives the felt underneath it — so the honest answer is often new underlayment and your existing tile reset, at a fraction of a tear-off. We'd rather tell you that and earn the next job.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Today we focus on one thing and do it properly: roofs — tile, shingle, flat and foam. Repair, replacement, underlayment and inspections, handled by our own team with no middlemen and nothing subcontracted out to a name you never agreed to.
            </p>
            <blockquote className="border-l-2 border-brand pl-5 italic text-ink/80">
              "We tell you what your roof actually needs — not what's easiest to sell. Then we put it in writing before anyone gets on the roof."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((p) => (
              <div key={p.title} className="card-lift bg-white/5 border border-white/10 rounded-2xl p-8">
                <span className="w-14 h-14 rounded-xl bg-brand flex items-center justify-center mb-5"><p.icon size={26} className="text-ink" /></span>
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-3">{p.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <span className="kicker">What We Stand For</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Our Core Values</h2>
          <div className="brand-line mx-auto mb-12" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {values.map((v) => (
              <div key={v} className="flex items-center gap-3 bg-secondary rounded-xl px-6 py-5 text-left">
                <span className="w-8 h-8 rounded-full bg-brand flex items-center justify-center shrink-0">
                  <Check size={16} className="text-ink" />
                </span>
                <span className="font-heading text-lg uppercase tracking-wide">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-dark border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-3 gap-8 items-end">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              {/* A word-length value like "Complimentary" cannot carry the
                  same display size as "28" — at text-5xl it runs past its
                  column. Long values step down rather than wrap mid-word. */}
              <div
                className={`font-heading text-brand mb-1 ${
                  s.value.length > 8
                    ? "text-2xl sm:text-2xl md:text-3xl"
                    : "text-3xl sm:text-4xl md:text-5xl"
                }`}
              >
                {s.value}
              </div>
              <div className="text-white/60 text-xs uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand text-ink text-center">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Let's Build Something That Lasts</h2>
          <p className="text-ink/90 mb-8 text-lg">Experience the Pepla difference on your next project.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-ink hover:bg-black text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
            Get a Free Estimate <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
